import { useEffect, useState } from "react";
import { DateTime } from "luxon";
import { Col, Form, Row, Spinner } from "react-bootstrap";
import { Line } from "react-chartjs-2";
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  ChartData,
} from "chart.js";
import useQueueService from "../../../hooks/useQueueService";
import { useAuthContext } from "../../auth/AuthContext";
import { useLayerContext } from "../../../context/Layer/LayerContext";

const ProcessingTimes = () => {
  ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
  );

  const { layers } = useLayerContext();

  const queueService = useQueueService();
  const [startDate, setStartDate] = useState(
    DateTime.now().startOf("day").minus({ days: 30 }),
  );
  const [endDate, setEndDate] = useState(DateTime.now().startOf("day"));
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState<
    ChartData<"line", number[], string> | undefined
  >();

  useEffect(() => {
    if (!layers) return;
    const abortController = new AbortController();
    setLoading(true);
    queueService
      .GetProcessingTimes(startDate, endDate, abortController)
      .then((data) => {
        setChartData({
          labels: data?.timeStamps?.map((x) => x.toFormat("dd/MM/yy")),
          datasets: layers.map((l, i) => ({
            label: l.displayName,
            data: data.values.map((d) => d[l.displayName] ?? 0),
            backgroundColor: selectBackgroundColor(i),
            borderColor: selectBorderColor(i),
          })),
        });
        setLoading(false);
      })
      .catch(() => {});

    return () => {
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, layers]);

  const selectBackgroundColor = (number: number): string => {
    const hue = number * 137.508; // use golden angle approximation
    return `hsl(${hue},50%,50%)`;
  };

  const selectBorderColor = (number: number): string => {
    const hue = number * 137.508; // use golden angle approximation
    return `hsl(${hue},50%,60%)`;
  };

  return (
    <>
      <Row>
        <Form.Group as={Col} sm={6}>
          <Form.Label>Start Date</Form.Label>
          <Form.Control
            type="date"
            value={startDate.toISODate()}
            onChange={(e) =>
              setStartDate(DateTime.fromISO(e.target.value) as DateTime)
            }
          />
        </Form.Group>
        <Form.Group as={Col} sm={6}>
          <Form.Label>End Date</Form.Label>
          <Form.Control
            type="date"
            min={startDate.toISODate()}
            value={endDate.toISODate()}
            onChange={(e) =>
              setEndDate(DateTime.fromISO(e.target.value) as DateTime)
            }
          />
        </Form.Group>
      </Row>
      <div className="d-flex w-100 h-100 " style={{ overflow: "auto" }}>
        <div className="h-100 w-100">
          {loading && <Spinner />}
          {chartData && (
            <Line
              data={chartData}
              options={{
                plugins: {
                  legend: {
                    position: "bottom",
                  },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    title: {
                      display: true,
                      text: "Average Processing Time (seconds) Per Tile",
                    },
                  },
                },
              }}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default ProcessingTimes;
